import _ from 'lodash';

import { LoanOptionVendorType } from 'constants/company';

export const getLoanOptionPendingData = ({
  pendingChanges,
  application,
  lockTerm,
  finalBuyPrice,
  programId,
  noteRate,
}) => {
  const initLockTerm = Number(_.get(pendingChanges, 'lockTerm.initialValue', application.lockTerm));
  const newLockTerm = Number(_.get(pendingChanges, 'lockTerm.newValue', application.lockTerm));
  const initFinalBuyPrice = Number(
    _.get(pendingChanges, 'finalBuyPrice.initialValue', application.finalBuyPrice),
  );
  const newFinalBuyPrice = Number(
    _.get(pendingChanges, 'finalBuyPrice.newValue', application.finalBuyPrice),
  );
  const initRate = Number(_.get(pendingChanges, 'rate.initialValue', application.rate));
  const newRate = Number(_.get(pendingChanges, 'rate.newValue', application.rate));
  const currentNoteRate = Number(noteRate);

  const newProgramId = _.get(
    pendingChanges,
    'programId.newValue',
    application.programId || application.productId,
  );

  const initProgramId = _.get(
    pendingChanges,
    'programId.initialValue',
    application.programId || application.productId,
  );

  const isInitLoanOptionId =
    programId === initProgramId &&
    lockTerm === initLockTerm &&
    finalBuyPrice === initFinalBuyPrice &&
    currentNoteRate === initRate;

  const isNewLoanOptionId =
    programId === newProgramId &&
    lockTerm === newLockTerm &&
    finalBuyPrice === newFinalBuyPrice &&
    currentNoteRate === newRate;

  return {
    isNewLoanOptionId,
    isInitLoanOptionId,
  };
};

export const getIsCellApplicable = ({
  cellData,
  program,
  lockTerm,
  loanLockTerm,
  isLoanLocked,
  isProgramIneligible,
  isObLoanRelockRequired,
  isProgramExpired,
  isExpiredPricingSaveEnabled,
}) => {
  if (!cellData) {
    return false;
  }

  // A program the engine denied cannot be priced onto the loan, so none of its cells may be picked. The caller passes
  // this only for an explicit denial: treating an absent eligibility payload as denied would dead-grid every program
  // whose payload omits it, which is the DEV-6518 lockout again (DEV-6521).
  if (isProgramIneligible) {
    return false;
  }

  const isObCellApplicable = !isLoanLocked && !isObLoanRelockRequired;

  // Expired OB pricing may be applied to carry a program on file, but only while the loan is
  // not locked/relock-required — locking on an expired price stays blocked server-side (FZ-4515).
  const isExpiredProgramSelectable = isExpiredPricingSaveEnabled && isObCellApplicable;

  if (isProgramExpired && !isExpiredProgramSelectable) {
    return false;
  }

  if (program?.vendorType === LoanOptionVendorType.OPTIMAL_BLUE) {
    if (isLoanLocked) {
      return loanLockTerm === lockTerm;
    }

    return isObCellApplicable || program?.lockPeriod === lockTerm;
  }

  // A locked loan is committed to ITS OWN lock term, so only that column stays selectable — opening the others would
  // let a click rewrite the lock term of a locked loan, and nothing downstream refuses that. Compare the loan's lock
  // term, not program.lockPeriod: that is the program's own default period, is absent on most programs, and gating on
  // it made every cell unselectable on a locked loan (DEV-6518).
  if (isLoanLocked && loanLockTerm !== lockTerm) {
    return false;
  }

  // Any price the sheet carries is selectable. Only the loan's own lock-term column carries a loanOptionId, and only
  // the rates a price bucket offered — requiring one is what made every other cell dead. A cell without one is applied
  // by price instead, so the only unselectable cell is one the sheet has no price for.
  return !_.isNil(cellData.finalBuyPrice);
};

export const getIsCellSelected = ({
  lockTerm,
  finalBuyPrice,
  selectedLoanProgramOption,
  programId,
  noteRate,
}) => {
  if (!selectedLoanProgramOption) {
    return false;
  }

  return (
    noteRate === selectedLoanProgramOption.rate &&
    lockTerm === selectedLoanProgramOption.lockTerm &&
    finalBuyPrice === selectedLoanProgramOption.finalBuyPrice &&
    programId === selectedLoanProgramOption.programId
  );
};
